/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui icons
import Check from '@material-ui/icons/Check';
// @material-ui/core components
import Radio from '@material-ui/core/Radio';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// global variables
import { userRanks, paidAdCurrency } from 'config';
// our components
import adCreateStyles from 'assets/jss/AdsCreate';
import { PROMOTED_AD, CREDIT_BUNDLE } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import InfoUser from 'components/common/InfoUser';
import Loading from 'components/common/Loading';
import UserSelect from 'components/User/Select';
import Datepicker from 'components/common/DatePicker';
import WithPermission from 'components/User/WithPermission';
import AuthContext from 'helpers/authContext';
import { allowPaidAdChangeCurrency } from 'helpers/functions';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */

const translateValidationSchema = (t, adUser) =>
  Yup.object({
    ad: Yup.string().required(t('errors.required')),
    fromOwner: Yup.boolean(),
    fromLoggedUser: Yup.boolean(),
    owner: Yup.object()
      .nullable()
      .when('fromLoggedUser', {
        is: val => val || !adUser,
        then: Yup.object()
          .nullable()
          .required(),
      }),
    package: Yup.object({
      id: Yup.string().required(),
    }),
    free: Yup.bool(),
  });

/**
 * Initial values to AdForm
 */
const initialValues = {
  ad: '',
  // eslint-disable-next-line no-underscore-dangle
  date: moment(),
  agency: null,
  owner: null,
  package: null,
  free: false,
  fromOwner: false,
  fromLoggedUser: false,
  currency: 'USD',
  secondaryCurrency: null,
};

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

class PromotedAdForm extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { adId, package: packageFound } = props;

    this.state = {
      fields: {
        ...initialValues,
        ad: adId || initialValues.ad,
        package: packageFound || initialValues.package,
      },
      showSelectAgency: false,
      showSelectOwner: false,
      exchangeRate: null,
    };
    this.formikRef = null;
    moment.locale(props.i18n.language);
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    const { bundleId } = this.props;
    if (bundleId) {
      history.push(CREDIT_BUNDLE.LIST);
    } else {
      history.push(PROMOTED_AD.LIST);
    }
  };

  /**
   * @description
   * Updating before send to parent.
   *
   * @param {*} values - New values to update fields.
   * @param {Object} user - User data.
   * @returns {*} Undefined - No returns.
   */
  handleSubmit = (values, user) => {
    const { onSave } = this.props;

    let finalValues = {
      ad: values.ad,
      package: values.package.id,
      owner: values.owner ? values.owner.id : undefined,
      free: values.free ? values.free : undefined,
      startDate: values.date.format() || null,
      secondaryCurrency: values.secondaryCurrency,
    };
    if (values.fromOwner || values.fromLoggedUser)
      return onSave(finalValues, true);
    finalValues = {
      ...finalValues,
      manager: values.agency ? values.agency.id : user.id,
    };
    return onSave(finalValues, false);
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   *
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    const { t } = this.props;
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
        if (item.code === 'no-packages') {
          all = [t('promoted_ad.form.no_packages')];
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  /**
   * @description
   * Show / Hide Agency (manager) field
   *
   * @param {Object} manager - Data of manager user.
   * @param {Function} handleChange - Passed from Formik.
   * @returns {*} - React node.
   */
  renderAgencyField = (manager, handleChange) => {
    const { classes, t } = this.props;
    const { showSelectAgency } = this.state;
    return (
      <div>
        <SweetAlert
          title=""
          showConfirm={false}
          showCancel={false}
          onConfirm={() => {}}
          customClass={classes.adModalConfirmation}
          show={showSelectAgency}
          closeOnClickOutside
          onCancel={() =>
            this.setState({
              showSelectAgency: !showSelectAgency,
            })
          }
        >
          <UserSelect
            listAgencies
            selectUser={agency => {
              handleChange({
                target: {
                  name: 'agency',
                  value: agency,
                },
              });
              // reset user owner
              if (!manager || (manager && agency.id !== manager.id)) {
                handleChange({
                  target: {
                    name: 'owner',
                    value: null,
                  },
                });
              }

              this.setState({
                showSelectAgency: !showSelectAgency,
              });
            }}
          />
        </SweetAlert>
        <div className={classes.section}>
          <h6>{t('promoted_ad.form.fields.agency')}</h6>
          <div className={classes.categorySection}>
            {manager && <InfoUser user={manager} />}
            <React.Fragment>
              <Button
                simple
                color="primary"
                onClick={() =>
                  this.setState({
                    showSelectAgency: true,
                  })
                }
              >
                {manager
                  ? t('ad.form.fields.change')
                  : t('ad.form.fields.select')}
              </Button>
              {manager && (
                <Button
                  simple
                  color="primary"
                  onClick={() => {
                    handleChange({
                      target: {
                        name: 'agency',
                        value: null,
                      },
                    });
                    handleChange({
                      target: {
                        name: 'owner',
                        value: null,
                      },
                    });
                  }}
                >
                  {t('promoted_ad.form.clean')}
                </Button>
              )}
            </React.Fragment>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      classes,
      errors: serverErrors,
      submitLoading,
      t,
      exchangeRates,
      closeNotification,
      onLoadPackages,
      onResetPackages,
      onResetAd,
      packages,
      onGetAd,
      adUser,
      adUserQueryDone,
      bundleId,
    } = this.props;
    const { showSelectOwner, fields, exchangeRate } = this.state;
    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t, adUser);

    return (
      <AuthContext.Consumer>
        {/* eslint-disable-next-line consistent-return */}
        {user => {
          const hasGeneral =
            user.allPermissions.filter(
              permission => permission.codename === 'add_promotedad',
            ).length === 1;
          return (
            <React.Fragment>
              <Snackbars
                place="tc"
                message={all || ''}
                open={!!all}
                color="danger"
                closeNotification={closeNotification}
                close
              />

              <Formik
                ref={ref => {
                  this.formikRef = ref;
                }}
                initialValues={fields}
                validationSchema={validationSchema}
                onSubmit={values => this.handleSubmit(values, user)}
                render={({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  handleBlur,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.pageContainer}>
                      <div
                        className={`${classes.row} ${classes.contentBtns}`}
                        style={{ justifyContent: 'space-between' }}
                      >
                        <h3>
                          {bundleId
                            ? `${t(
                                'promoted_ad.form.create_with_bundle',
                              )} ${bundleId}`
                            : t('promoted_ad.form.create')}
                        </h3>
                      </div>

                      <Card>
                        <CardBody>
                          <Loading show={submitLoading} />
                          <TextField
                            label={t('promoted_ad.form.fields.id')}
                            name="ad"
                            error={touched.ad && !!errors.ad}
                            helperText={touched.ad ? errors.ad : ''}
                            disabled={!!bundleId}
                            InputProps={{
                              value: values.ad,
                              onChange: e => {
                                handleChange({
                                  target: {
                                    name: 'package',
                                    value: null,
                                  },
                                });
                                handleChange({
                                  target: {
                                    name: 'agency',
                                    value: null,
                                  },
                                });
                                handleChange({
                                  target: {
                                    name: 'owner',
                                    value: null,
                                  },
                                });
                                onResetPackages();
                                onResetAd();
                                handleChange(e);
                              },
                              onBlur: handleBlur,
                              type: 'number',
                            }}
                          />
                          <Button
                            id="get-ad"
                            color="primary"
                            simple
                            disabled={adUserQueryDone}
                            onClick={() => {
                              handleChange({
                                target: {
                                  name: 'package',
                                  value: null,
                                },
                              });
                              onGetAd(values.ad, user);
                            }}
                          >
                            {t('promoted_ad.form.button_get_ad')}
                          </Button>
                          {adUserQueryDone && (
                            <>
                              <WithPermission
                                allowPermissions={[
                                  'make_self_owner_promotedad',
                                ]}
                              >
                                <div style={{ marginTop: 15 }}>
                                  <FormControlLabel
                                    control={
                                      <Radio
                                        onChange={() => {
                                          handleChange({
                                            target: {
                                              name: 'fromLoggedUser',
                                              value: false,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'fromOwner',
                                              value: false,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'agency',
                                              value: null,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'owner',
                                              value: null,
                                            },
                                          });
                                          handleChange({
                                            target: {
                                              name: 'package',
                                              value: null,
                                            },
                                          });
                                          onResetPackages();
                                        }}
                                        checked={
                                          !values.fromLoggedUser &&
                                          !values.fromOwner
                                        }
                                        name="fromLoggedUser"
                                        checkedIcon={
                                          <Check
                                            className={classes.radioChecked}
                                          />
                                        }
                                        icon={
                                          <Check
                                            className={classes.radioUnchecked}
                                          />
                                        }
                                        className={classes.radio}
                                      />
                                    }
                                    label={t(
                                      'promoted_ad.form.fields.fromAgency',
                                    )}
                                  />
                                  {!adUser && (
                                    <>
                                      <FormControlLabel
                                        control={
                                          <Radio
                                            onChange={() => {
                                              handleChange({
                                                target: {
                                                  name: 'fromLoggedUser',
                                                  value: true,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'fromOwner',
                                                  value: false,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'agency',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'owner',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'package',
                                                  value: null,
                                                },
                                              });
                                              onResetPackages();
                                              onLoadPackages(values.ad, user);
                                            }}
                                            checked={values.fromLoggedUser}
                                            name="fromLoggedUser"
                                            checkedIcon={
                                              <Check
                                                className={classes.radioChecked}
                                              />
                                            }
                                            icon={
                                              <Check
                                                className={
                                                  classes.radioUnchecked
                                                }
                                              />
                                            }
                                            className={classes.radio}
                                          />
                                        }
                                        label={t(
                                          'promoted_ad.form.fields.fromLoggedUser',
                                        )}
                                      />
                                      <FormControlLabel
                                        control={
                                          <Radio
                                            onChange={() => {
                                              handleChange({
                                                target: {
                                                  name: 'fromLoggedUser',
                                                  value: false,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'fromOwner',
                                                  value: true,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'agency',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'owner',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'package',
                                                  value: null,
                                                },
                                              });
                                              onResetPackages();
                                            }}
                                            checked={values.fromOwner}
                                            name="fromOwner"
                                            checkedIcon={
                                              <Check
                                                className={classes.radioChecked}
                                              />
                                            }
                                            icon={
                                              <Check
                                                className={
                                                  classes.radioUnchecked
                                                }
                                              />
                                            }
                                            className={classes.radio}
                                          />
                                        }
                                        label={t(
                                          'promoted_ad.form.fields.fromOwner',
                                        )}
                                      />
                                    </>
                                  )}
                                </div>
                              </WithPermission>
                              {values.fromOwner && (
                                <React.Fragment>
                                  <SweetAlert
                                    title=""
                                    showConfirm={false}
                                    showCancel={false}
                                    onConfirm={() => {}}
                                    customClass={classes.adModalConfirmation}
                                    show={showSelectOwner}
                                    closeOnClickOutside
                                    onCancel={() =>
                                      this.setState({
                                        showSelectOwner: !showSelectOwner,
                                      })
                                    }
                                  >
                                    <UserSelect
                                      selectUser={owner => {
                                        handleChange({
                                          target: {
                                            name: 'owner',
                                            value: owner,
                                          },
                                        });
                                        this.setState({
                                          showSelectOwner: !showSelectOwner,
                                        });
                                        handleChange({
                                          target: {
                                            name: 'package',
                                            value: null,
                                          },
                                        });
                                        onResetPackages();
                                        onLoadPackages(values.ad, owner);
                                      }}
                                    />
                                  </SweetAlert>
                                  <div
                                    className={classes.section}
                                    style={{ marginBottom: 32 }}
                                  >
                                    <h6
                                      style={{
                                        color:
                                          touched.owner && errors.owner
                                            ? 'red'
                                            : 'black',
                                      }}
                                    >
                                      {t(
                                        'promoted_ad.form.fields.owner_no_agency',
                                      )}
                                    </h6>
                                    <div className={classes.row}>
                                      {values.owner && (
                                        <InfoUser user={values.owner} />
                                      )}
                                      <React.Fragment>
                                        <Button
                                          simple
                                          color="primary"
                                          id="selectOwner"
                                          onClick={() => {
                                            this.setState({
                                              showSelectOwner: true,
                                            });
                                          }}
                                        >
                                          {values.owner
                                            ? t('ad.form.fields.change')
                                            : t('ad.form.fields.select')}
                                        </Button>
                                        {values.owner && (
                                          <Button
                                            simple
                                            color="primary"
                                            onClick={() => {
                                              handleChange({
                                                target: {
                                                  name: 'owner',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'package',
                                                  value: null,
                                                },
                                              });
                                              onResetPackages();
                                            }}
                                          >
                                            {t('promoted_ad.form.clean')}
                                          </Button>
                                        )}
                                      </React.Fragment>
                                    </div>
                                  </div>
                                </React.Fragment>
                              )}
                              {!values.fromOwner &&
                                !values.fromLoggedUser &&
                                (hasGeneral || user.rank === userRanks.ADMIN) &&
                                this.renderAgencyField(values.agency, arg => {
                                  handleChange(arg);
                                  handleChange({
                                    target: {
                                      name: 'package',
                                      value: null,
                                    },
                                  });
                                  onResetPackages();
                                  if (arg.target.name === 'agency')
                                    onLoadPackages(
                                      values.ad,
                                      null,
                                      arg.target.value
                                        ? arg.target.value.id
                                        : null,
                                    );
                                })}
                              {/* show owner selection modal  */}
                              {!values.fromOwner && !values.fromLoggedUser && (
                                <React.Fragment>
                                  <SweetAlert
                                    title=""
                                    showConfirm={false}
                                    showCancel={false}
                                    onConfirm={() => {}}
                                    customClass={classes.adModalConfirmation}
                                    show={showSelectOwner}
                                    closeOnClickOutside
                                    onCancel={() =>
                                      this.setState({
                                        showSelectOwner: !showSelectOwner,
                                      })
                                    }
                                  >
                                    {values.agency ? (
                                      <UserSelect
                                        agency={values.agency}
                                        selectUser={owner => {
                                          handleChange({
                                            target: {
                                              name: 'owner',
                                              value: owner,
                                            },
                                          });
                                          this.setState({
                                            showSelectOwner: !showSelectOwner,
                                          });
                                          handleChange({
                                            target: {
                                              name: 'package',
                                              value: null,
                                            },
                                          });
                                          onResetPackages();
                                          onLoadPackages(
                                            values.ad,
                                            owner,
                                            values.agency.id,
                                          );
                                        }}
                                      />
                                    ) : (
                                      <UserSelect
                                        listUserAgencies
                                        selectUser={owner => {
                                          handleChange({
                                            target: {
                                              name: 'owner',
                                              value: owner,
                                            },
                                          });
                                          this.setState({
                                            showSelectOwner: !showSelectOwner,
                                          });
                                          handleChange({
                                            target: {
                                              name: 'package',
                                              value: null,
                                            },
                                          });
                                          onResetPackages();
                                          onLoadPackages(
                                            values.ad,
                                            owner,
                                            user.id,
                                          );
                                        }}
                                      />
                                    )}
                                  </SweetAlert>
                                  <div
                                    className={classes.section}
                                    style={{ marginBottom: 32 }}
                                  >
                                    <h6
                                      style={{
                                        color:
                                          touched.owner && errors.owner
                                            ? 'red'
                                            : 'black',
                                      }}
                                    >
                                      {adUser
                                        ? t('promoted_ad.form.fields.user')
                                        : t('promoted_ad.form.fields.owner')}
                                    </h6>
                                    <div className={classes.row}>
                                      {values.owner && (
                                        <InfoUser user={values.owner} />
                                      )}
                                      {!values.owner &&
                                        adUser &&
                                        adUser.fullname}
                                      {!values.owner &&
                                        adUser &&
                                        !adUser.fullname &&
                                        `${t(
                                          'promoted_ad.form.fields.private_user',
                                        )} (${adUser.fakeId})`}
                                      {!adUser && (
                                        <React.Fragment>
                                          <Button
                                            simple
                                            color="primary"
                                            id="selectOwner"
                                            onClick={() => {
                                              this.setState({
                                                showSelectOwner: true,
                                              });
                                            }}
                                          >
                                            {values.owner
                                              ? t('ad.form.fields.change')
                                              : t('ad.form.fields.select')}
                                          </Button>
                                          {values.owner && (
                                            <Button
                                              simple
                                              color="primary"
                                              onClick={() => {
                                                handleChange({
                                                  target: {
                                                    name: 'owner',
                                                    value: null,
                                                  },
                                                });
                                                handleChange({
                                                  target: {
                                                    name: 'package',
                                                    value: null,
                                                  },
                                                });
                                                onResetPackages();
                                              }}
                                            >
                                              {t('promoted_ad.form.clean')}
                                            </Button>
                                          )}
                                        </React.Fragment>
                                      )}
                                    </div>
                                  </div>
                                </React.Fragment>
                              )}
                              <GridContainer>
                                {packages.length > 0 && (
                                  <GridItem xs={12} sm={6}>
                                    <Datepicker
                                      title={t('promoted_ad.form.fields.date')}
                                      defaultValue=""
                                      required
                                      fromToday
                                      dateTime
                                      onChange={date => {
                                        handleChange({
                                          target: {
                                            name: 'date',
                                            value: date || '',
                                          },
                                        });
                                      }}
                                      value={values.date}
                                    />
                                  </GridItem>
                                )}
                              </GridContainer>
                              {packages.length > 0 && (
                                <div className={classes.section}>
                                  <h6
                                    style={{
                                      color:
                                        touched.package &&
                                        errors.package &&
                                        (!values.package ||
                                          !values.package.locationScope)
                                          ? 'red'
                                          : 'black',
                                    }}
                                  >
                                    {t('promoted_ad.form.fields.locationScope')}
                                  </h6>
                                  {packages
                                    .map(p => p.locationScope)
                                    .filter(
                                      (locationScope, index, self) =>
                                        self.indexOf(locationScope) === index,
                                    )
                                    .map((locationScope, key) => (
                                      <Button
                                        key={`locationScope-${locationScope}`}
                                        simple={
                                          values.package
                                            ? locationScope !==
                                              values.package.locationScope
                                            : true
                                        }
                                        round
                                        id={`locationScope-${key}`}
                                        color="info"
                                        onClick={() => {
                                          handleChange({
                                            target: {
                                              name: 'package',
                                              value: {
                                                locationScope,
                                              },
                                            },
                                          });
                                        }}
                                        disabled={
                                          bundleId &&
                                          !packages.some(
                                            p =>
                                              p.locationScope ===
                                                locationScope &&
                                              p.finalPrice.discounts.includes(
                                                'credit_bundle',
                                              ),
                                          )
                                        }
                                      >
                                        {t(
                                          // eslint-disable-next-line max-len
                                          `promoted_ad.form.fields.${locationScope.toLowerCase()}`,
                                        )}
                                      </Button>
                                    ))}
                                </div>
                              )}
                              {values.package && (
                                <>
                                  <div className={classes.section}>
                                    <h6
                                      style={{
                                        color:
                                          touched.package &&
                                          errors.package &&
                                          (!values.package ||
                                            !values.package.id)
                                            ? 'red'
                                            : 'black',
                                      }}
                                    >
                                      {t('promoted_ad.form.fields.duration')}
                                    </h6>

                                    {packages
                                      .filter(
                                        p =>
                                          p.locationScope ===
                                          values.package.locationScope,
                                      )
                                      .map((p, key) => (
                                        <Button
                                          key={`duration-${ReadableDate(
                                            p.duration,
                                          )}`}
                                          id={`duration-${key}`}
                                          simple={
                                            values.package
                                              ? p.id !== values.package.id
                                              : true
                                          }
                                          round
                                          color="info"
                                          onClick={() => {
                                            handleChange({
                                              target: {
                                                name: 'package',
                                                value: p,
                                              },
                                            });
                                          }}
                                          disabled={
                                            bundleId &&
                                            !p.finalPrice.discounts.includes(
                                              'credit_bundle',
                                            )
                                          }
                                        >
                                          {ReadableDate(p.duration)}
                                        </Button>
                                      ))}
                                  </div>
                                  <div className={classes.section}>
                                    <div>
                                      <WithPermission
                                        allowPermissionFunc={
                                          allowPaidAdChangeCurrency
                                        }
                                      >
                                        <div className={classes.section}>
                                          <h6>
                                            {t(
                                              'promoted_ad.form.fields.currency',
                                            )}
                                          </h6>
                                          {Object.keys(paidAdCurrency).map(
                                            (currency, key) => {
                                              const rate = exchangeRates.find(
                                                value =>
                                                  value.currencyFrom ===
                                                    fields.currency &&
                                                  value.currencyTo === currency,
                                              );
                                              if (
                                                fields.currency !== currency &&
                                                !rate
                                              )
                                                return null;

                                              return (
                                                <Button
                                                  key={`currency-${currency}`}
                                                  simple={
                                                    fields.secondaryCurrency
                                                      ? fields.secondaryCurrency !==
                                                        currency
                                                      : fields.currency !==
                                                        currency
                                                  }
                                                  round
                                                  id={`currency-${key}`}
                                                  color="info"
                                                  onClick={() => {
                                                    handleChange({
                                                      target: {
                                                        name:
                                                          'secondaryCurrency',
                                                        value:
                                                          fields.currency !==
                                                          currency
                                                            ? currency
                                                            : null,
                                                      },
                                                    });
                                                    this.setState({
                                                      fields: {
                                                        ...fields,
                                                        secondaryCurrency:
                                                          fields.currency !==
                                                          currency
                                                            ? currency
                                                            : null,
                                                      },
                                                      exchangeRate:
                                                        fields.currency !==
                                                        currency
                                                          ? rate.rate
                                                          : null,
                                                    });
                                                  }}
                                                >
                                                  {currency}
                                                </Button>
                                              );
                                            },
                                          )}
                                        </div>
                                      </WithPermission>
                                    </div>
                                    <div>
                                      <h6>
                                        {t('promoted_ad.form.fields.price')}
                                      </h6>
                                      {values.free ? (
                                        <p>
                                          {`0 ${
                                            values.secondaryCurrency
                                              ? values.secondaryCurrency
                                              : values.currency
                                          }`}
                                        </p>
                                      ) : (
                                        values.package.finalPrice && (
                                          <p>
                                            {values.secondaryCurrency
                                              ? `${
                                                  exchangeRate
                                                    ? values.package.finalPrice
                                                        .price * exchangeRate
                                                    : ''
                                                } ${values.secondaryCurrency}`
                                              : // eslint-disable-next-line max-len
                                                `${values.package.finalPrice.price} ${values.currency}`}
                                          </p>
                                        )
                                      )}
                                    </div>
                                    <div>
                                      <h6>
                                        {t('promoted_ad.form.fields.discounts')}
                                      </h6>
                                      {values.package.finalPrice &&
                                        values.package.finalPrice.discounts.map(
                                          discount => (
                                            <p>
                                              {t(
                                                // eslint-disable-next-line max-len
                                                `promoted_ad.form.fields.discountTypes.${discount}`,
                                              )}
                                            </p>
                                          ),
                                        )}
                                    </div>
                                  </div>
                                </>
                              )}
                              {packages.length > 0 && values.package && (
                                <WithPermission
                                  allowPermissions={[
                                    'make_free_promotedad',
                                    'make_free_ownership_promotedad',
                                  ]}
                                >
                                  <div style={{ marginTop: 15 }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          onClick={e => {
                                            handleChange(e);
                                          }}
                                          checked={values.free}
                                          name="free"
                                          checkedIcon={
                                            <Check
                                              className={classes.checkedIcon}
                                            />
                                          }
                                          icon={
                                            <Check
                                              className={classes.uncheckedIcon}
                                            />
                                          }
                                          classes={{
                                            checked: classes.checked,
                                          }}
                                        />
                                      }
                                      label={t('promoted_ad.form.fields.free')}
                                    />
                                  </div>
                                </WithPermission>
                              )}
                              <div className={classes.section}>
                                <GridContainer
                                  direction="row-reverse"
                                  style={{
                                    justifyContent:
                                      packages.length > 0
                                        ? 'space-between'
                                        : 'center',
                                  }}
                                >
                                  {packages.length > 0 && (
                                    <GridItem xs={12} sm={6}>
                                      <div
                                        className={classes.contentBtns}
                                        style={{
                                          flex: 'None',
                                        }}
                                      >
                                        <Button
                                          id="save-ad"
                                          color="primary"
                                          type="submit"
                                        >
                                          {t('promoted_ad.form.button_create')}
                                        </Button>
                                      </div>
                                    </GridItem>
                                  )}
                                  <GridItem
                                    xs={12}
                                    sm={6}
                                    style={{
                                      flex: 'None',
                                    }}
                                  >
                                    <Button
                                      id="cancel-create"
                                      simple
                                      color="danger"
                                      onClick={this.handleCancelBtn}
                                    >
                                      {t('common.cancel')}
                                    </Button>
                                  </GridItem>
                                </GridContainer>
                              </div>
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </form>
                )}
              />
            </React.Fragment>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

/**
 * AdForm PropTypes
 * @param classes - styles received from withStyles
 * @param ad - Entity data
 * @param editMode - edition mode
 * @param errors - Errors data list
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with language information
 * @param exchangeRateQuery - Query for get an ExchangeRate
 * @callback onSave - callback called when user clicks save button
 * @callback onLoadPackages - callback when user clicks load packages button
 * @callback onResetPackages - callback when user clicks reset packages button
 * @param packages - Packages data list
 */

PromotedAdForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onLoadPackages: PropTypes.func.isRequired,
  onResetPackages: PropTypes.func.isRequired,
  packages: PropTypes.instanceOf(Array).isRequired,
  exchangeRates: PropTypes.instanceOf(Array).isRequired,
  closeNotification: PropTypes.func.isRequired,
  adUser: PropTypes.instanceOf(Object),
  adUserQueryDone: PropTypes.bool,
  package: PropTypes.instanceOf(Object),
  onGetAd: PropTypes.func.isRequired,
  onResetAd: PropTypes.func.isRequired,
  adId: PropTypes.string,
  bundleId: PropTypes.string,
};

PromotedAdForm.defaultProps = {
  adUserQueryDone: false,
  adUser: null,
  package: null,
  adId: null,
  bundleId: null,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PromotedAdFormWrapper = props => <PromotedAdForm {...props} />;

export default withTranslation()(
  withStyles(adCreateStyles)(PromotedAdFormWrapper),
);
